import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Palette, 
  BarChart3, 
  Settings as SettingsIcon, 
  Shield,
  Menu,
  X,
  LogOut,
  Crown,
  Building2,
  DollarSign,
  Activity,
  Globe
} from 'lucide-react'
import OperatorManagement from './OperatorManagement'
import OperatorDetail from './OperatorDetail'
import SubscriptionManagement from './SubscriptionManagement'
import BrandingManagement from './BrandingManagement'
import PlatformAnalytics from './PlatformAnalytics'
import SystemConfiguration from './SystemConfiguration'
import SuperAdminOverview from './SuperAdminOverview'

interface SuperAdminDashboardProps {
  onLogout?: () => void
}

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [user, setUser] = useState({
    name: 'Platform Administrator',
    email: 'Loading...',
    role: 'Super Admin'
  })

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      try {
        const storedUser = localStorage.getItem('operator_user')
        if (storedUser) {
          const userData = JSON.parse(storedUser)
          setUser(prev => ({
            ...prev,
            email: userData.email || 'admin@ai-booking-engine.com',
            name: userData.full_name || 'Platform Administrator'
          }))
        }
      } catch {
        // JSON parse failed -- keep defaults
      }
    }
  }, [])
  const location = useLocation()
  const navigate = useNavigate()

  const navigation = [
    { 
      name: 'Platform Overview', 
      href: '/super-admin', 
      icon: LayoutDashboard, 
      current: location.pathname === '/super-admin',
      description: 'Dashboard overview and metrics'
    },
    { 
      name: 'B2B Operators', 
      href: '/super-admin/operators', 
      icon: Building2, 
      current: location.pathname.includes('/operators'),
      description: 'Manage tour operators and travel agencies'
    },
    { 
      name: 'Subscriptions & Billing', 
      href: '/super-admin/subscriptions', 
      icon: CreditCard, 
      current: location.pathname.includes('/subscriptions'),
      description: 'Subscription plans and payment management'
    },
    { 
      name: 'Branding Management', 
      href: '/super-admin/branding', 
      icon: Palette, 
      current: location.pathname.includes('/branding'),
      description: 'Custom branding for B2B operators'
    },
    { 
      name: 'Platform Analytics', 
      href: '/super-admin/analytics', 
      icon: BarChart3, 
      current: location.pathname.includes('/analytics'),
      description: 'Revenue, usage, and performance metrics'
    },
    { 
      name: 'System Configuration', 
      href: '/super-admin/configuration', 
      icon: SettingsIcon, 
      current: location.pathname.includes('/configuration'),
      description: 'System settings and integrations'
    }
  ]

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-center h-16 px-4" style={{ background: ADMIN_BRAND.gradient }}>
          <div className="flex items-center">
            <Crown className="h-8 w-8 text-yellow-300 mr-2" />
            <div className="text-white">
              <h1 className="text-lg font-bold">Super Admin</h1>
              <p className="text-xs text-blue-100">Platform Control</p>
            </div>
          </div>
        </div>
        
        <nav className="mt-5 px-2 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  item.current
                    ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } group flex items-start px-3 py-3 text-sm font-medium rounded-l-lg transition-colors`}
              >
                <Icon
                  className={`${
                    item.current ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                  } flex-shrink-0 h-5 w-5 mr-3`}
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                </div>
              </Link>
            )
          })}
        </nav>

        {/* Platform Status */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <Activity className="h-4 w-4 text-green-500 mr-2" />
              <div className="text-xs">
                <div className="text-green-800 font-medium">Platform Status</div>
                <div className="text-green-600">All Systems Operational</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" aria-hidden="true">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="bg-white shadow-sm z-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button
                  type="button"
                  className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <span className="sr-only">Open sidebar</span>
                  {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
                
                <div className="ml-4 lg:ml-0">
                  <h1 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Globe className="h-5 w-5 text-blue-600 mr-2" />
                    AI Booking Engine Platform
                  </h1>
                  <p className="text-xs text-gray-500">Super Administrator Dashboard</p>
                </div>
              </div>

              {/* User menu */}
              <div className="flex items-center space-x-4">
                {/* Platform stats */}
                <div className="hidden sm:flex items-center space-x-4 text-sm">
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></div>
                    Online
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex items-center">
                    <Shield className="h-8 w-8 text-blue-600 bg-blue-100 rounded-full p-1.5" />
                    <div className="ml-3 hidden sm:block">
                      <div className="text-sm font-medium text-gray-700">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.role}</div>
                    </div>
                  </div>
                  
                  <button
                    onClick={onLogout}
                    className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Routes>
                <Route path="/" element={<SuperAdminOverview />} />
                <Route path="/operators" element={<OperatorManagement />} />
                <Route path="/operators/:operatorId" element={<OperatorDetail />} />
                <Route path="/subscriptions/*" element={<SubscriptionManagement />} />
                <Route path="/branding/*" element={<BrandingManagement />} />
                <Route path="/analytics/*" element={<PlatformAnalytics />} />
                <Route path="/configuration/*" element={<SystemConfiguration />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SuperAdminDashboard